import { Spinner } from "react-bootstrap";
import AppLayout from "../layout/AppLayout";
import ProductItem from "./ProductItem";
import { useEffect, useState } from "react";
import Cookies from "js-cookie";
import regenerateToken from "../regenerateToken";
import { buildRequestConfig } from "../../utils/apiHelper";
import { isAdmin as checkAdmin } from "../../utils/authUtils";
import axios from "axios";
import { positions, useAlert } from "react-alert";
import Loader from "../loader/loader";
import { useAccessConfig } from "../layout/AccessContext";
import { useAdmin } from "../layout/AdminContext";
import AccessRestricted from "../layout/AccessRestricted";
import { FaPlus, FaTrashAlt, FaEdit, FaExternalLinkAlt, FaSave, FaImage } from 'react-icons/fa';
import './productRecommendations.css';

const ProductRecommendationConfig = () => {
  const { accessConfig, loading: accessLoading } = useAccessConfig();
  const { isAdmin } = useAdmin();

  const [show, setShow] = useState(false);
  const [recommendationData, setRecommendationData] = useState([]);
  const [selectedCard, setSelectedCard] = useState("");
  const [imageError, setImageError] = useState(false);
  const [loader, setLoader] = useState(false);
  const [dataLoader, setDataLoader] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [isNewProduct, setIsNewProduct] = useState(false);
  const alert = useAlert();

  const getProducts = async () => {
    const url = "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/recommendation/product";
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({ method: "GET", url }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get("idToken");
    return await axios({
      headers: { authorization: idToken },
      method: "GET",
      url,
      validateStatus: (s) => s >= 200 && s <= 401,
    });
  };

  const updateStateData = (data) => {
    let modifiedData = data.map((each) => ({
      ...each,
      id: crypto.randomUUID(),
    }));
    setRecommendationData(modifiedData);
  };

  const fetchProductsData = async () => {
    setDataLoader(true);
    try {
      const existingProductsResponse = await getProducts();

      const statusCode = existingProductsResponse.status;
      if (statusCode === 401) {
        await regenerateToken();
        const retryResponse = await getProducts();
        const data = retryResponse.data;
        if (data?.data?.reco) {
          updateStateData(data.data.reco);
        } else {
          setRecommendationData([]);
          alert.error(<span className="alertError">{data?.message}</span>, {
            positions: positions.TOP_CENTER,
          });
        }
      } else {
        const data = existingProductsResponse.data;

        if (data?.data?.reco) {
          updateStateData(data.data.reco);
        } else {
          setRecommendationData([]);
        }
      }
    } catch (error) {
      console.log("error", error.message);
      alert.error(
        <span className="alertError">
          Failed to load patient details data. Please try again
        </span>,
        {
          positions: positions.TOP_CENTER,
        }
      );
    } finally {
      setDataLoader(false);
    }
  };

  useEffect(() => {
    // Only fetch if user has access (admins always have access)
    if (!accessLoading && (isAdmin || (accessConfig && accessConfig.productRecommendations))) {
      fetchProductsData();
    }
  }, [accessLoading, accessConfig?.productRecommendations, isAdmin]);

  // Show loading spinner while access config is being fetched
  if (accessLoading) {
    return (
      <AppLayout>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </AppLayout>
    );
  }

  // Access control check - show restricted page if no access (after all hooks)
  // Admins are never restricted
  if (!isAdmin && (!accessConfig || !accessConfig.productRecommendations)) {
    return <AccessRestricted featureName="Product Recommendations" />;
  }

  const handleClose = () => {
    setShow(false);
  };

  const handleProductAddition = () => {
    setShow(true);
    setIsNewProduct(true);
    setSelectedCard({
      id: crypto.randomUUID(),
      name: "",
      description: "",
      icon: "",
      linked_tests: {
        low: [],
        high: [],
      },

      recommended: false,
      url: "",
    });
  };

  const postRecommendationData = async () => {
    const url = "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/recommendation/product";
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({ method: "POST", url, data: recommendationData }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get("idToken");
    return await axios({
      headers: { authorization: idToken },
      method: "POST",
      url,
      data: JSON.stringify(recommendationData),
      validateStatus: (s) => s >= 200 && s <= 401,
    });
  };

  const handleSubmit = async (e) => {
    if (!recommendationData?.length) {
      alert.info("No recommendation data to add");
      return;
    }
    setLoader(true);
    try {
      const response = await postRecommendationData();
      if (response.status === 401) {
        await regenerateToken();
        const retryResponse = await postRecommendationData();
        const data = retryResponse.data;
        if (data.isSuccess) {
          alert.success(<span>Data updated successfully!</span>, {
            position: positions.TOP_CENTER,
          });
        }
      } else {
        const data = response.data;

        if (data.isSuccess) {
          alert.success(<span>Data updated successfully!</span>, {
            position: positions.TOP_CENTER,
          });
        }
      }
    } catch (e) {
      alert.error(
        <span className="alertError">
          Something went wrong. Please try again.
        </span>,
        {
          position: positions.TOP_CENTER,
        }
      );
    } finally {
      setLoader(false);
    }
  };

  const deleteProduct = (product) => {
    let filteredProducts = recommendationData.filter(
      (each) => each.id !== product.id
    );
    setRecommendationData(filteredProducts);
    alert.info("Product deleted successfully");
  };

  const deleteRecommendationData = async () => {
    const url = "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/recommendation/product";
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({ method: "DELETE", url }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get("idToken");
    return await axios({
      headers: { authorization: idToken },
      method: "DELETE",
      url,
      validateStatus: (s) => s >= 200 && s <= 401,
    });
  };

  const handleDelete = async (e) => {
    if (!recommendationData?.length) {
      alert.info("No data to delete");
      return;
    }
    setDeleteLoader(true);
    try {
      const response = await deleteRecommendationData();
      if (response.status === 401) {
        await regenerateToken();
        const retryResponse = await deleteRecommendationData();
        const data = retryResponse.data;
        if (data.isSuccess) {
          alert.success(<span>Data deleted successfully!</span>, {
            position: positions.TOP_CENTER,
          });
          fetchProductsData();
        }
      } else {
        const data = response.data;

        if (data.isSuccess) {
          alert.success(<span>Data deleted successfully!</span>, {
            position: positions.TOP_CENTER,
          });
          fetchProductsData();
        }
      }
    } catch (e) {
      alert.error(
        <span className="alertError">
          Something went wrong. Please try again.
        </span>,
        {
          position: positions.TOP_CENTER,
        }
      );
    } finally {
      setDeleteLoader(false);
    }
  };

  return (
    <AppLayout>
      {dataLoader ? (
        <Loader />
      ) : (
        <div className="pr-container">
          {/* Header */}
          <header className="pr-header">
            <div className="pr-title-group">
              <h1 className="pr-title">Product Recommendations</h1>
              <p className="pr-subtitle">Manage recommended products linked to test results.</p>
            </div>
            <div className="pr-actions-top">
              <button
                className="pr-btn-danger-outline"
                onClick={handleDelete}
                disabled={deleteLoader || !recommendationData?.length}
                title="Delete all from server"
              >
                {deleteLoader ? <Spinner animation="border" size="sm" /> : "Clear All"}
              </button>
              <button
                className="pr-btn-primary"
                onClick={handleSubmit}
                disabled={loader || !recommendationData?.length}
              >
                {loader ? <Spinner animation="border" size="sm" /> : <><FaSave /> Save Changes</>}
              </button>
            </div>
          </header>

          {/* Products Grid */}
          <div className="pr-grid">

            {/* 1. Add New Card */}
            <div className="pr-add-card" onClick={handleProductAddition}>
              <div className="pr-add-icon"><FaPlus /></div>
              <div className="pr-add-text">Add New Product</div>
            </div>

            {/* 2. Product Cards */}
            {recommendationData?.length > 0 && recommendationData.map((each) => (
              <div className="pr-card" key={each.id}>
                <div className="pr-card-img-wrapper">
                  {!imageError && each.icon ? (
                    <img
                      src={each.icon}
                      alt={each.name}
                      className="pr-card-img"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  {/* Fallback layout if img fails or no icon */}
                  <div className="pr-no-image" style={{ display: each.icon && !imageError ? 'none' : 'flex' }}>
                    <FaImage size={32} style={{ opacity: 0.3 }} />
                    <div style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>No Image Preview</div>
                  </div>
                </div>

                <div className="pr-card-body">
                  <h3 className="pr-card-title" title={each.name}>{each.name}</h3>
                  <p className="pr-card-desc" title={each.description}>
                    {each.description || "No description provided."}
                  </p>

                  <div className="pr-card-footer">
                    <a href={each.url} target="_blank" rel="noopener noreferrer" className="pr-link">
                      <FaExternalLinkAlt size={12} /> View Product
                    </a>
                    <div className="pr-card-actions">
                      <button
                        className="pr-action-btn pr-btn-edit"
                        onClick={() => {
                          setSelectedCard(each);
                          setShow(true);
                          setIsNewProduct(false);
                        }}
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        className="pr-action-btn pr-btn-delete"
                        onClick={() => deleteProduct(each)}
                      >
                        <FaTrashAlt size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

          </div>

          {/* Empty State visual if no products (excluding the Add card which is always there) */}
          {(!recommendationData || recommendationData.length === 0) && (
            <div style={{ marginTop: '2rem', textAlign: 'center', color: '#94a3b8' }}>
              <p>No products added yet. Click the card above to create one.</p>
            </div>
          )}

          <ProductItem
            show={show}
            handleClose={handleClose}
            selectedCard={selectedCard}
            setRecommendationData={setRecommendationData}
            isNewProduct={isNewProduct}
            recommendationData={recommendationData}
            setIsNewProduct={setIsNewProduct}
          />
        </div>
      )}
    </AppLayout>
  );
};

export default ProductRecommendationConfig;
